/**************************************************************************************************************************
 Copyright (c) 2023 WorkEarly Results LLP

     Name: seo-head.tsx
   Author: Harikrishnan Gangadharan
 Comments: 

/**************************************************************************************************************************
 IMPORTS
***************************************************************************************************************************/
import { type FunctionComponent } from 'react';
import Helmet from 'react-helmet';

import SEOMetadata from '@/constants/seo';
/**************************************************************************************************************************
 TYPES / GLOBAL DEFINITIONS
***************************************************************************************************************************/
export interface SEOHeadProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  ogType: string;
  ogImgUrl?: string;
  ogImgWidth?: string;
  ogImgHeight?: string;
  structuredData: string;
  children?: any;
}
/**************************************************************************************************************************
 IMPLEMENTATIONS
***************************************************************************************************************************/
const SEOHead: FunctionComponent<SEOHeadProps> = ({
  title = SEOMetadata.siteName,
  description = SEOMetadata.description,
  canonicalUrl = SEOMetadata.siteUrl,
  ogType,
  ogImgUrl = SEOMetadata.siteLogo,
  ogImgWidth = SEOMetadata.siteLogoWidth,
  ogImgHeight = SEOMetadata.siteLogoHeight,
  structuredData,
  children = undefined
}: SEOHeadProps) => {
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <link rel="icon" href="/favicon.ico" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:site_name" content={SEOMetadata.siteName} />
      <meta property="og:type" content={ogType} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImgUrl} />
      <meta property="og:image:width" content={ogImgWidth} />
      <meta property="og:image:height" content={ogImgHeight} />
      <meta property="og:url" content={canonicalUrl} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: structuredData }}
        key="item-jsonld"
      />
      {children}
    </Helmet>
  );
};

export default SEOHead;
